import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '@uzurv/rideWss/app/services';
import { isNullOrUndefined, PhoneNumberUtil } from '@uzurv/rideWss/app/util';
import { AuthenticationStatus } from '@uzurv/rideWss/auth/constants';

interface ToolbarTab {
  id: string;
  label: string;
  link: string;
}

@Component({
  selector: 'app-toolbar',
  standalone: true,
  imports: [NgFor, NgIf, MatToolbarModule, MatTabsModule, MatButtonModule, MatIconModule, RouterLink, RouterLinkActive],
  template: `
    <mat-toolbar>
      <div id="toolbar-container-context" class="toolbar-content">
        <img id="toolbar-image-uzurvLogo" src="/assets/ride-wss-app/imgs/UZURV-Logo_Primary.png" aria-hidden="true" class="logo" />
        <nav
          *ngIf="authenticationStatus === AuthenticationStatus.LOGGED_IN && showTabs"
          id="toolbar-nav-context"
          mat-tab-nav-bar
          [tabPanel]="tabPanel"
        >
          <a
            *ngFor="let tab of tabs; let i = index"
            [id]="'toolbar-nav-link-' + tab.id"
            role="tab"
            #rla="routerLinkActive"
            routerLinkActive
            mat-tab-link
            [active]="rla.isActive"
            [routerLink]="tab.link"
            [attr.aria-label]="getTabAriaLabel(tab, i)"
          >
            {{ tab.label }}
          </a>
        </nav>

        <ng-container *ngIf="authenticationStatus === AuthenticationStatus.LOGGED_IN">
          <ng-container *ngIf="shouldShowHelpButton; else logoutButton">
            <button id="toolbar-button-needHelp" mat-stroked-button color="primary" (click)="handleNeedHelpClicked()">
              <mat-icon id="toolbar-icon-needHelp" svgIcon="uz-help"></mat-icon>
              <span id="toolbar-text-needHelp">{{ this.translateService.instant('toolbar.button.help') }}</span>
            </button>
          </ng-container>

          <ng-template #logoutButton>
            <button id="toolbar-button-logout" mat-stroked-button color="primary" (click)="handleLogoutClicked()">
              <span id="toolbar-text-logout">{{ this.translateService.instant('myAccount.button.logout') }}</span>
            </button>
          </ng-template>
        </ng-container>
      </div>
    </mat-toolbar>
    <mat-tab-nav-panel #tabPanel class="toolbar-tab-panel"></mat-tab-nav-panel>
  `,
  styles: [
    `
      mat-toolbar {
        background-color: var(--rss-primary-white);
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.25);
        min-height: 4.8rem;
      }

      .toolbar-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0 1.5rem 0 0.5rem;
        min-height: 4.8rem;
        box-sizing: border-box;
        width: 100%;
      }

      .logo {
        height: 1.5rem;
        align-self: center;
        margin-top: 1rem;
      }

      nav[mat-tab-nav-bar] {
        padding: 0 0 0.3rem 2rem;
        align-self: center;
        margin-top: 2.5rem;
      }

      button {
        margin-bottom: 0.5rem;
        margin-left: auto;
        margin-top: 1rem;
      }

      button .mat-icon {
        font-size: 1.125rem;
        width: 1.125rem;
        height: 1.125rem;
        line-height: 1.125rem;
      }

      .toolbar-tab-panel {
        display: none;
      }

      @media (max-width: 600px) {
        mat-toolbar {
          height: auto;
        }

        .toolbar-content {
          flex-wrap: wrap;
          padding: 0.75rem 0 0 0.5rem;
          row-gap: 0.5rem;
          align-items: center;
        }

        .logo {
          width: 156px;
          height: 25px;
          margin-top: 0;
          margin-bottom: 0;
          flex: 0 0 auto;
        }

        button {
          margin-top: 0;
          margin-left: auto;
          order: 2;
        }

        nav[mat-tab-nav-bar] {
          order: 3;
          flex: 1 0 100%;
          margin-top: 0;
          margin-right: 0;
          padding: 0;
        }

        nav[mat-tab-nav-bar] .mat-mdc-tab-link {
          flex: 0 0 auto;
        }
      }
    `
  ]
})
export class ToolbarComponent {
  @Input() authenticationStatus: AuthenticationStatus;
  @Input() bookingPhoneNumber: string;
  @Input() showTabs: boolean = true;
  @Input() shouldShowHelpButton: boolean = true;

  @Output() logoutButtonClicked = new EventEmitter<boolean>();

  readonly tabs: Array<ToolbarTab> = [
    { id: 'rides', label: 'My Rides', link: '/rides' },
    { id: 'account', label: 'Account', link: '/account' }
  ];
  readonly AuthenticationStatus = AuthenticationStatus;

  constructor(private modalService: ModalService, public translateService: TranslateService) {}

  getTabAriaLabel(tab: ToolbarTab, index: number): string {
    return `${tab.label}, ${index + 1} of ${this.tabs.length}`;
  }

  handleNeedHelpClicked(): void {
    void this.modalService.presentNeedHelpModal(this.getFormattedPhoneNumber(this.bookingPhoneNumber));
  }

  handleLogoutClicked(): void {
    this.logoutButtonClicked.emit();
  }

  private getFormattedPhoneNumber(phoneNumber: string): string {
    if (!isNullOrUndefined(phoneNumber) && PhoneNumberUtil.isValidPhoneNumber(phoneNumber, 'US')) {
      return PhoneNumberUtil.formattedPhoneNumberForDisplay(phoneNumber, 'US');
    }

    return phoneNumber;
  }
}
